import type { OrderTag } from 'src/services/tags';

export type TagOption = {
  value: string;
  label: string;
  color: string;
  isGlobal: boolean;
  isCreate?: boolean;
};

export const CREATE_OPTION_VALUE = '__create__';

// Mirrors the backend normalization (trim + lowercase) used for the
// idempotent POST /tags match — keep in sync with crm.tag._normalize_tag_name.
export const normalizeTagName = (name: string): string => name.trim().toLowerCase();

export const mapTagsToOptions = (tags: OrderTag[], assignedIds: number[]): TagOption[] =>
  tags
    .filter(tag => !assignedIds.includes(tag.id))
    .map(tag => ({
      value: String(tag.id),
      label: tag.name,
      color: tag.color,
      isGlobal: tag.is_global,
    }));

export const hasExactMatch = (tags: OrderTag[], input: string): boolean => {
  const normalized = normalizeTagName(input);
  return tags.some(tag => normalizeTagName(tag.name) === normalized);
};

export const buildCreateOption = (input: string): TagOption => ({
  value: CREATE_OPTION_VALUE,
  label: input.trim(),
  color: '',
  isGlobal: false,
  isCreate: true,
});

export type TagChipColors = { backgroundColor: string; color: string };

const TAG_PALETTE: Array<[string, string]> = [
  ['#f9d9de', '#96233a'],
  ['#fbe3c4', '#7d4a06'],
  ['#f1ecc0', '#5f540a'],
  ['#d3ecd9', '#1f5737'],
  ['#c9e8ec', '#0f545c'],
  ['#d4e0fa', '#1f439c'],
  ['#e5d3f2', '#56307d'],
  ['#f8d3e7', '#8c2a64'],
];

// Deterministic name→palette hash from the design prototype:
// h = (h*31 + charCode) % 997 per char; palette[h % 8].
// Chip colors are derived from the tag NAME, not the backend-stored tag.color.
export const getTagChipColors = (name: string): TagChipColors => {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) {
    h = (h * 31 + name.charCodeAt(i)) % 997;
  }
  const [backgroundColor, color] = TAG_PALETTE[h % TAG_PALETTE.length];
  return { backgroundColor, color };
};
